import type { MarketplaceCardState } from "./marketplace-status";
import { isRelationshipStage } from "./relationship-stage";
import {
  TARGET_CONFIRM_LINE,
  TARGET_CONFIRM_LINK_LABEL,
} from "./target-confirm";
import type { WaitlistPosition } from "./waitlist-api";

/**
 * Plan 435 M2 (mfh-hero-state). Pure derivation of the marketplace-first
 * Home hero's state from the existing connection status machine
 * (`deriveMarketplaceCardState`, reused verbatim — never re-derived) plus
 * three honest inputs: `isLive` (`portalDiscoverable`, never `publishedAt`),
 * `areaLaunched` (browse `access !== "area_not_launched"`, never waitlist
 * membership), and the offers/agent counts. See tech-spec Concepts
 * "Hero state" / "No target area" and the design doc §5.2 for the full
 * copy table this mirrors.
 *
 * Fixed precedence (first match wins), per the roadmap/tech-spec:
 *   connected › finalizing_escape › finalizing › waiting_agent › no_target
 *   › area_not_launched › live_offers › live_quiet › unpublished_agents
 *   › unpublished_none
 *
 * Invariants:
 *   - `pro_name` is set ONLY for `connected` and `waiting_agent` — every
 *     other branch must never read a name onto the output.
 *   - No payment vocabulary (`fee`, `charged`, `$300`, `payment`) appears in
 *     any copy string.
 *   - `finalizing` and `finalizing_escape` share byte-identical sub copy —
 *     they differ only in whether a `secondary` (Cancel) CTA is present.
 *     Since both derive from `cardState.kind` (itself collapsing
 *     charging/held into one kind, never branching on raw status), the
 *     buyer can never distinguish `charging` from `held` through this
 *     function either.
 */

export type HomeHeroStateKind =
  | "connected"
  | "finalizing_escape"
  | "finalizing"
  | "waiting_agent"
  | "no_target"
  | "area_not_launched"
  | "live_offers"
  | "live_quiet"
  | "unpublished_agents"
  | "unpublished_none";

export interface HomeHeroCta {
  label: string;
}

export interface HomeHeroState {
  kind: HomeHeroStateKind;
  eyebrow: string;
  headline: string;
  sub: string;
  primary?: HomeHeroCta;
  secondary?: HomeHeroCta;
  /** Present ONLY at `connected` and `waiting_agent` — see module doc. */
  proName?: string;
  proPhotoUrl?: string;
  /** Avatar-stack count (CelebrationBanner idiom) — the pro at `connected`,
   *  the competing-agent count at the live/unpublished/waiting states, 0
   *  elsewhere. */
  avatarCount: number;
  /** Passed straight through from the input — every state carries it so a
   *  body block below the hero can still render offers even when the hero
   *  itself shows a different headline (e.g. `waiting_agent` + offers). */
  offersCount: number;
  /** `finalizing_escape` only — threads the connection id to the Cancel
   *  confirm dialog without the caller re-deriving it from `cardState`. */
  connectionId?: string;
  /** `connected` only — the pro's `users.id`, read straight through from
   *  `cardState.proUserId` (reveal-gated to active). The caller uses this to
   *  build the `/threads?pro=<proUserId>` Message deep link (the existing
   *  Your Team shape — `ConsumerMessagesView` resolves it) rather than
   *  inventing a second CTA destination scheme. */
  proUserId?: string;
  /** Server-provided stage, used only to label the connected guide link. */
  relationshipStage?: string;
}

export interface DeriveHomeHeroStateInput {
  cardState: MarketplaceCardState;
  /** `snapshot.portalDiscoverable` — never `publishedAt`. */
  isLive: boolean;
  /** Whether the buyer has an active target area set at all. */
  hasTarget: boolean;
  /** Browse `access !== "area_not_launched"` — never waitlist membership. */
  areaLaunched: boolean;
  /**
   * Plan 454 (decision 519): browse `access === "target_unresolved"` - the
   * target is set but has no resolved ZIP (a legacy row). Maps to the SAME
   * `no_target` kind as a missing target, with the confirm-your-target copy
   * instead of the set-a-target copy. Never reads as `area_not_launched`.
   */
  targetUnresolved?: boolean;
  waitlistPosition: WaitlistPosition | null;
  offersCount: number;
  agentCount: number;
  /** Target area label, used in eyebrows/copy. Empty string degrades the
   *  copy gracefully (no "· " dangling separator is emitted). */
  area: string;
  /** Buyer tier string (e.g. "t1") — used only in `unpublished_agents`
   *  copy; omitted renders the tier-agnostic phrasing. */
  tier?: string;
  /** The request's `pro_display_name` (plan 435 M0 additive field) — the
   *  ONLY source of a name at `waiting_agent`. `cardState`'s own
   *  `agentDisplayName` is a different, session-only field and is
   *  deliberately not read here. */
  proDisplayName?: string;
  /** The request's `expires_at` (plan 435 M0 additive field) — the 72h
   *  buyer-consent deadline. Used ONLY at `waiting_agent`. Absent/unparseable
   *  degrades the sub-copy to its first sentence only — never an "Invalid
   *  Date" or an empty placeholder. */
  expiresAt?: string;
  /**
   * `connection.checkin?.pending` (plan 435 M4 iteration-2 fix item 2) —
   * used ONLY at `connected`. Decision 527 (2026-10-07): the headline stays
   * the standard connected headline either way (one ask per viewport — the
   * question lives in the check-in card below, never the hero); only the
   * sub-line changes to point at it. Superseded by `relationshipStage ===
   * "closed"` in the fixed precedence (deal closed > check-in due > plain
   * connected) — see `deriveHomeHeroState`'s connected branch.
   */
  checkinPending?: boolean;
}

function eyebrowWithArea(prefix: string, area: string): string {
  return area ? `${prefix} · ${area}` : prefix;
}

/** First name only — the pro's full display name may be "Priya Nair"; the
 *  `Message {first}` CTA (design §5.2 row 1) reads only "Priya". Falls back
 *  to the full string if it has no internal whitespace. */
function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName;
}

function connectedNextStep(
  stage: "connected" | "agreement" | "active" | "under_contract",
  first: string,
): string {
  const who = first || "your agent";
  switch (stage) {
    case "connected":
      return `You're connected. Message ${who} to plan a first conversation.`;
    case "agreement":
      return `${first || "Your agent"} marked your buyer agreement as signed. Ask for a copy if you don't have one.`;
    case "active":
      return `Compare homes and share what matters most with ${who}.`;
    case "under_contract":
      return `Stay in touch with ${who} as the contract process moves forward.`;
  }
}

/** Short absolute date ("Thu, Oct 9") for the `waiting_agent` expiry
 *  sentence. Returns null on a missing/unparseable `expiresAt` so the caller
 *  can degrade to the first sentence only — never "Invalid Date". */
function formatExpiresDate(expiresAt: string | undefined): string | null {
  if (!expiresAt) return null;
  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function waitlistSub(position: WaitlistPosition): string {
  if (position.revealed) {
    return typeof position.position === "number"
      ? `You're #${position.position} on the list — share your link to move up.`
      : "You're on the list — share your link to move up.";
  }
  return `${position.count} buyer${position.count === 1 ? "" : "s"} are waiting here — be the one who unlocks it.`;
}

const FINALIZING_SUB =
  "Usually within a day. If we can't finalize, you'll be released on day 7.";

export function deriveHomeHeroState(
  input: DeriveHomeHeroStateInput,
): HomeHeroState {
  const {
    cardState,
    isLive,
    hasTarget,
    areaLaunched,
    targetUnresolved = false,
    waitlistPosition,
    offersCount,
    agentCount,
    area,
    tier,
    proDisplayName,
    expiresAt,
    checkinPending,
  } = input;

  // 1. connected — status === "active". The ONLY other state that may ever
  //    carry a pro name is `waiting_agent` (case 4 below). Three copy
  //    variants share this one kind (design §5.3 "Hero copy for the three
  //    connected stops differs only in headline/sub-line"; CTAs and the
  //    readiness chip stay constant) — fixed precedence: deal closed >
  //    check-in due > plain connected.
  if (cardState.kind === "connected-payoff") {
    const name = cardState.proName.trim();
    const first = firstName(name);
    const dealClosed = cardState.relationshipStage === "closed";
    const headline = dealClosed
      ? `Congratulations on closing${first ? ` with ${first}` : ""}`
      : name
        ? `You're working with ${name}`
        : "You're connected with an agent";
    const sub = dealClosed
      ? "Leave a review while it's fresh."
      : checkinPending
        ? `Quick check-in below — how's it going with ${first || "your agent"}?`
        : cardState.relationshipStage &&
            isRelationshipStage(cardState.relationshipStage) &&
            cardState.relationshipStage !== "closed"
          ? connectedNextStep(cardState.relationshipStage, first)
          : `You're connected. Message ${first || "your agent"} to decide what comes next.`;
    return {
      kind: "connected",
      eyebrow: eyebrowWithArea("CONNECTED", area),
      headline,
      sub,
      primary: { label: first ? `Message ${first}` : "Message agent" },
      secondary: { label: "View team" },
      proName: cardState.proName,
      proPhotoUrl: cardState.proPhotoUrl,
      proUserId: cardState.proUserId,
      relationshipStage: cardState.relationshipStage,
      avatarCount: 1,
      offersCount,
    };
  }

  // 2. finalizing_escape — charging/held + escape_eligible.
  if (cardState.kind === "finalizing-escape") {
    return {
      kind: "finalizing_escape",
      eyebrow: "CONNECTING",
      headline: "Finalizing your connection",
      sub: FINALIZING_SUB,
      secondary: { label: "Cancel request" },
      avatarCount: 0,
      offersCount,
      connectionId: cardState.connectionId,
    };
  }

  // 3. finalizing — charging/held, no escape yet. Byte-identical sub to
  //    case 2; the only difference is the absent `secondary` CTA.
  if (cardState.kind === "finalizing") {
    return {
      kind: "finalizing",
      eyebrow: "CONNECTING",
      headline: "Finalizing your connection",
      sub: FINALIZING_SUB,
      avatarCount: 0,
      offersCount,
    };
  }

  // 4. waiting_agent — a pending_pro standing-offer request.
  if (cardState.kind === "waiting-agent") {
    const expiresDate = formatExpiresDate(expiresAt);
    return {
      kind: "waiting_agent",
      eyebrow: eyebrowWithArea("REQUEST SENT", area),
      headline: proDisplayName
        ? `Waiting on ${proDisplayName}`
        : "Waiting on the agent",
      sub: expiresDate
        ? `Agents have 72 hours to respond. Requests expire ${expiresDate}.`
        : "Agents have 72 hours to respond.",
      primary: { label: "View request" },
      secondary: { label: "Browse other agents" },
      proName: proDisplayName,
      avatarCount: agentCount,
      offersCount,
    };
  }

  // 5. no_target — outranks area_not_launched: the server's fail-closed
  //    "not launched" answer for an empty target area must never render as
  //    a geography claim.
  if (!hasTarget || targetUnresolved) {
    if (hasTarget) {
      return {
        kind: "no_target",
        eyebrow: "CONFIRM YOUR TARGET",
        headline: TARGET_CONFIRM_LINE,
        sub: "Pick your target city or ZIP again so Sill can match you with agents nearby.",
        primary: { label: TARGET_CONFIRM_LINK_LABEL },
        avatarCount: 0,
        offersCount,
      };
    }
    return {
      kind: "no_target",
      eyebrow: "SET YOUR TARGET",
      headline: "Set your target area to see the agents competing near you",
      sub: "Sill matches you with agents once it knows where you're buying.",
      primary: { label: "Set target area" },
      avatarCount: 0,
      offersCount,
    };
  }

  // 6. area_not_launched — browse access says the metro isn't live yet.
  if (!areaLaunched) {
    const hasRow = waitlistPosition !== null;
    return {
      kind: "area_not_launched",
      eyebrow: eyebrowWithArea("WAITLIST", area),
      headline: area ? `Sill isn't in ${area} yet` : "Sill isn't here yet",
      sub: hasRow
        ? waitlistSub(waitlistPosition)
        : "Be the one who unlocks it for other buyers nearby.",
      primary: hasRow
        ? { label: "Share your link" }
        : { label: "Join the waitlist" },
      // Secondary (never primary — the hand-raise feeds the demand map):
      // a buyer whose target ZIP is outside every launched metro can browse
      // a live one by editing the target area. `HomeView.ctaHref` routes it
      // to the target-area row (`TARGET_CONFIRM_HREF`);
      // `BrowseWaitlistState` carries the matching block.
      secondary: { label: "Change target area" },
      avatarCount: 0,
      offersCount,
    };
  }

  // 7. live_offers. Headline leads with the agent count ("n agents are
  //    competing for you") — the marketing framing (design §9: "saving
  //    money through competing agents"). Open question Q1: when offers
  //    exist but browse's agent count has dropped to 0 (every agent
  //    withdrew/paused), fall back to the offer count instead.
  if (isLive && offersCount > 0) {
    return {
      kind: "live_offers",
      eyebrow: eyebrowWithArea("LIVE", area),
      headline:
        agentCount > 0
          ? `${agentCount} agent${agentCount === 1 ? " is" : "s are"} competing for you`
          : `${offersCount} offer${offersCount === 1 ? "" : "s"} waiting for you`,
      sub: "Compare what each agent is offering.",
      primary: { label: "View offers" },
      secondary: { label: "Browse agents" },
      avatarCount: agentCount,
      offersCount,
    };
  }

  // 8. live_quiet
  if (isLive) {
    return {
      kind: "live_quiet",
      eyebrow: eyebrowWithArea("LIVE", area),
      headline:
        agentCount > 0
          ? `${agentCount} agent${agentCount === 1 ? "" : "s"} can see your passport`
          : "Your passport is live",
      sub: "Standing offers land here as agents respond.",
      primary: { label: "Browse agents" },
      secondary: { label: "Share passport" },
      avatarCount: agentCount,
      offersCount,
    };
  }

  // 9. unpublished_agents
  if (agentCount > 0) {
    const tierCopy = tier ? `a ${tier.toUpperCase()}` : "an";
    return {
      kind: "unpublished_agents",
      eyebrow: eyebrowWithArea("NOT LIVE", area),
      headline: `${agentCount} agent${agentCount === 1 ? " has" : "s have"} ${tierCopy} offer waiting for you`,
      sub: "Go live and they can send it.",
      primary: { label: "Go live to agents" },
      secondary: { label: "Browse agents" },
      avatarCount: agentCount,
      offersCount,
    };
  }

  // 10. unpublished_none — the fallback.
  return {
    kind: "unpublished_none",
    eyebrow: eyebrowWithArea("NOT LIVE", area),
    headline: "Your passport isn't live yet",
    sub: area
      ? `Agents serving ${area} will see it once you publish.`
      : "Agents will see it once you publish.",
    primary: { label: "Go live to agents" },
    secondary: { label: "Browse agents" },
    avatarCount: 0,
    offersCount,
  };
}
